import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";
import { THAI_VISA_TYPES, getThaiVisaType, type ThaiVisaType } from "@/content/visa-corpus/thai-visa-types";

function faqsEn(v: ThaiVisaType) {
  return [
    { q: `Who can apply for the ${v.titleEn}?`, a: v.whoEn },
    { q: "What is the difference between visa validity and permitted stay?", a: `${v.validityEn} — ${v.stayEn}` },
    { q: "Which documents are required?", a: v.requirementsEn.join(" · ") },
    { q: "What is the application process?", a: v.processEn.join(" → ") },
    { q: "How do I extend my stay in Thailand?", a: v.extensionEn },
    { q: "What are the most common reasons for refusal?", a: v.pitfallsEn.join(" · ") },
    {
      q: "Which authority decides the application?",
      a: `${v.authorityEn} — every approval is at the discretion of the deciding officer; we do not guarantee outcomes.`,
    },
    {
      q: "How much does your service cost?",
      a: "Fees depend on the visa category, document volume and filing location. Contact our team by phone, LINE or email for a quotation for your case.",
    },
  ];
}

export const Route = createFileRoute("/en/visa/thai/$type")({
  loader: ({ params }) => {
    const v = getThaiVisaType(params.type);
    if (!v) throw notFound();
    return { v };
  },
  component: ThaiVisaTypePageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Visa type not found" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { v } = loaderData;
    const title = `${v.titleEn} 2026 — Requirements, Documents & Extensions`;
    const description = `${v.titleEn} (${v.codeEn}) — who qualifies, required documents, filing steps, extension of stay and common pitfalls. Referenced to the Thai Immigration Bureau and Ministry of Foreign Affairs.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/visa/thai/${params.type}`,
        type: "article",
        alternates: [{ lang: "th", path: `/visa/thai/${params.type}` }],
        keywords: [v.titleEn, v.codeEn, `${v.titleEn} requirements`, `${v.titleEn} extension`, "Thailand visa 2026", "TM.7 extension"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Thai Visa Types", url: `${site}/en/visa/thai` },
              { name: v.titleEn, url: `${site}/en/visa/thai/${params.type}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(v))) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `How to apply for ${v.titleEn}`, description: `Step-by-step application route for ${v.titleEn} (${v.codeEn ?? v.codeTh}) under Thai Immigration practice.`, path: `/en/visa/thai/${params.type}`, steps: v.processEn, inLanguage: "en" })) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/visa/thai/${params.type}`)) },
      ],
    };
  },
});

function ThaiVisaTypePageEN() {
  const { v } = Route.useLoaderData();
  const faqs = faqsEn(v);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/visa/thai" className="hover:underline">Thai Visa Types</a> <span>/</span>{" "}
          <span>{v.titleEn}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{v.titleEn}</h1>
        <p className="mb-6 text-muted-foreground">{v.whoEn}</p>
        <p className="mb-6 text-sm text-muted-foreground">
          <a href={`/visa/thai/${v.slug}`} className="text-primary underline">ภาษาไทย →</a>
        </p>

        <div className="mb-8 grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border bg-card p-4 text-sm">
            <div className="mb-1 font-semibold">Visa validity</div>
            <p className="text-muted-foreground">{v.validityEn}</p>
          </div>
          <div className="rounded-lg border bg-card p-4 text-sm">
            <div className="mb-1 font-semibold">Permitted length of stay</div>
            <p className="text-muted-foreground">{v.stayEn}</p>
          </div>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Eligibility and documents</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.requirementsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Application process</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {v.processEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Extension of stay in Thailand</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{v.extensionEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Common pitfalls</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — {v.titleEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other Thai visa categories</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {THAI_VISA_TYPES.filter((x) => x.slug !== v.slug).map((x) => (
              <a key={x.slug} href={`/en/visa/thai/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">Talk to our team</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Ask about eligibility and service fees for your own case by phone, LINE or email — Mon–Sat 9:00–18:00 ICT.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">Contact us →</a>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Sources: {v.sources.map((s: { label: string }) => s.label).join(" · ")} — criteria, processing times and permitted stay can change by
          official announcement, and every approval is at the discretion of the deciding officer. Please verify with the responsible authority
          before acting.
        </p>

        <FaqSourceNote topic="visa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
